import { Box, Table, TableBody } from '@mui/material';
import Loading from 'components/common/Loading';
import Pagination from 'components/controls/Pagination';
import { connect } from 'formik';
import { arrayOf, func, object, string } from 'prop-types';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Company from 'services/api/Company';
import { useListQuery } from 'store/app/entities/EmployeesApiSlice';
import authCompanyId from 'store/selectors/authCompanyId';
import authEmployee from 'store/selectors/authEmployee';
import EmployeeRow from './EmployeeRow';
import TableHead from './TableHead';
import Toolbar from './Toolbar';

const TABLE_HEAD_HEIGHT = 52;
const ROW_HEIGHT = 48;
const DEFAULT_LIMIT = 10;

const union = (value, added, by, base) => {
  return [
    ...value,
    ...added
      .filter(a => !value.some(v => (by ? v[by] : v) === a))
      .map(a => (by ? { ...base, [by]: a } : a)),
  ];
};

const EmployeesTable = ({
  error = null,
  value,
  defaultRole = null,
  name,
  onChange,
  formik: { values },
}) => {
  const [order, setOrder] = React.useState({ by: 'name', isReverse: false });
  const [page, setPage] = React.useState(0);
  const [search, setSearch] = React.useState('');
  const [limit, setLimit] = useState(null);
  const tableContainerRef = useRef(null);
  const companyId = useSelector(authCompanyId);

  useLayoutEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const { height } = el.getBoundingClientRect();
    if (height > 0) {
      const newLimit = Math.max(1, Math.floor((height - TABLE_HEAD_HEIGHT) / ROW_HEIGHT));
      setLimit(newLimit);
    }
  }, []);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(entries => {
      const { height } = entries[0].contentRect;
      const newLimit = Math.max(1, Math.floor((height - TABLE_HEAD_HEIGHT) / ROW_HEIGHT));
      setLimit(prev => (prev !== newLimit ? newLimit : prev));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [limit]);

  const effectiveLimit = limit ?? DEFAULT_LIMIT;

  const {
    data: { data: employeesWithMe, metadata } = {},
    isLoading,
    isFetching,
  } = useListQuery(
    {
      companyId,
      search,
      skip: page * effectiveLimit,
      limit: effectiveLimit,
      order: (order.isReverse ? '-' : '') + order.by,
      team: values._id,
    },
    { skip: limit === null },
  );
  const employee = useSelector(authEmployee);
  const ruleManaged = (values.ruleCount || 0) > 0;
  const employees = useMemo(
    () => employeesWithMe?.filter(e => e._id !== employee?._id) ?? [],
    [employee?._id, employeesWithMe],
  );

  const selectAll = async enabled => {
    if (ruleManaged) return;
    const data = (await Company.getEmployeesIds(search, true)).filter(e => e !== employee?._id);
    if (enabled) {
      onChange(
        name,
        union(values[name], data, 'employee', { role: defaultRole, isVisible: false }),
      );
      onChange(
        `${name}Removed`,
        values[`${name}Removed`].filter(e => value.some(v => v === e)),
      );
      onChange(
        `${name}Added`,
        union(values[`${name}Added`] || [], data, 'employee', {
          role: defaultRole,
          isVisible: false,
        }),
      );
    } else {
      if (search) {
        onChange(
          name,
          values[name].filter(v => !data.includes(v.employee)),
        );
        onChange(
          `${name}Added`,
          values[`${name}Added`].filter(v => !data.includes(v.employee)),
        );
      } else {
        onChange(name, []);
        onChange(`${name}Added`, []);
      }
      onChange(`${name}Removed`, union(values[`${name}Removed`], data));
    }
  };

  const toggleSelect = e => {
    if (ruleManaged) return;
    if (value.find(member => member.employee === e)) {
      onChange(
        name,
        value.filter(member => member.employee !== e),
      );
      onChange(`${name}Removed`, [...values[`${name}Removed`], e]);
      onChange(
        `${name}Added`,
        values[`${name}Added`].filter(v => v.employee !== e),
      );
    } else {
      onChange(name, [...value, { employee: e, role: defaultRole, isVisible: false }]);
      onChange(
        `${name}Removed`,
        values[`${name}Removed`].filter(_id => _id !== e),
      );
      onChange(`${name}Added`, [
        ...values[`${name}Added`],
        { employee: e, role: defaultRole, isVisible: false },
      ]);
    }
  };

  const sort = by => {
    setOrder(currentOrder => ({ by, isReverse: !currentOrder.isReverse }));
  };

  const onSearch = newSearch => {
    setSearch(newSearch);
    setPage(0);
  };

  const changePage = ({ selected }) => {
    setPage(selected);
  };

  const updateMember = (e, updates) => {
    const newValue = value.map(m => (m.employee === e._id ? { ...m, ...updates } : m));
    onChange(name, newValue);
    const member = newValue.find(m => m.employee === e._id);
    const newValueAdded = [...values[`${name}Added`].filter(m => m.employee !== e._id), member];
    onChange(`${name}Added`, newValueAdded);
  };

  const selectedCount = value.length;

  const employeesCount = metadata?.total || 0;
  const showLoading = isLoading || limit === null;

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Toolbar error={error} selected={selectedCount} onSearch={onSearch} isFetching={isFetching} />
      <Box ref={tableContainerRef} sx={{ flex: 1, overflow: 'hidden', minHeight: 0, minWidth: 0 }}>
        {showLoading ? (
          <Loading />
        ) : (
          <Table
            aria-labelledby="employeesTable"
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              '& .MuiTableCell-root': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                pl: 1,
                pr: 1,
              },
            }}
          >
            <TableHead
              order={order}
              selected={selectedCount}
              rows={employeesCount}
              onSort={sort}
              onSelectAll={selectAll}
              disabled={ruleManaged}
            />
            <TableBody>
              {employees.map(e => (
                <EmployeeRow
                  key={e._id}
                  employee={e}
                  defaultRole={defaultRole}
                  member={value.find(m => m.employee === e._id)}
                  onSelect={toggleSelect}
                  onUpdateMember={updateMember}
                  ruleManaged={ruleManaged}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
      <Box sx={{ mt: 1, flexShrink: 0 }}>
        <Pagination
          total={employeesCount}
          page={page}
          perPage={effectiveLimit}
          onChange={changePage}
        />
      </Box>
    </Box>
  );
};

EmployeesTable.propTypes = {
  name: string.isRequired,
  value: arrayOf(string).isRequired,
  error: string,
  defaultRole: string,
  onChange: func.isRequired,
  formik: object.isRequired,
};

export default connect(EmployeesTable);
